"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.migrate = exports.migratePrepare = exports.MNDE_NFT_CREATOR = exports.MNDE_REALM = exports.MNDE_MINT = exports.THIRTY_DAYS_IN_SECONDS = void 0;
const solana_contrib_1 = require("@saberhq/solana-contrib");
const web3_js_1 = require("@solana/web3.js");
const VsrClient_1 = require("./vsr-client/VsrClient");
const js_1 = require("@metaplex-foundation/js");
const bn_js_1 = require("bn.js");
const solana_spl_token_modern_1 = require("solana-spl-token-modern");
const sdk_1 = require("./sdk");
const Escrow_1 = require("./Escrow");
const spl_governance_1 = require("@solana/spl-governance");
exports.THIRTY_DAYS_IN_SECONDS = 30 * 24 * 60 * 60;
exports.MNDE_MINT = new web3_js_1.PublicKey('MNDEFzGvMt87ueuHvVU9VcTqsAP5b3fTGPsHuuPA5ey');
exports.MNDE_REALM = new web3_js_1.PublicKey('899YG3yk4F66ZgbNWLHriZHTXSKk9e1kvsKEquW7L6Mo');
exports.MNDE_NFT_CREATOR = new web3_js_1.PublicKey('6jG2QcwaJPFS8Y9SzgH2kfKPj6ERhLi9RVtH8kRahj4j');
/**
 * Creates preparation transactions to setup VSR plugin accounts
 * needed for particular wallet public key
 * (verification of the Registrar address, creating Voter account, configuration of deposit entry)
 * and cleaning state of the escrow relocker
 * (closing voting).
 *
 * Emitted transactions are expected all to be processed before any transactions from the migrate method.
 *
 * @param provider Solana provider of @saberhq/solana-contrib, the wallet key in the provider is taken as the owner of NFTs to be migrated
 * @param nftCreator NFT creator address that filters what NFTs, owned by provider.wallet, are migrated
 * @param splGovRealmMint SPL Governance Realm mint to migrate with, default set to MNDE mint
 * @param splGovRealm SPL Governance Realm address to migrate to, default set to MNDE Realm
 * @param splGovVsrProgramId  SPL Governance Voter Stage Registry program ID configured at Realm, default set to one at MNDE Realm
 */
async function migratePrepare({ provider, nftCreator = exports.MNDE_NFT_CREATOR, splGovRealmMint = exports.MNDE_MINT, splGovRealm = exports.MNDE_REALM, splGovVsrProgramId = VsrClient_1.MNDE_VSR_PROGRAM_ID, nftMintsTokens, }) {
    const wallet = provider.wallet.publicKey;
    let tx = new solana_contrib_1.TransactionEnvelope(provider, [], []);
    // caller may pass in the NFTs to migrate, when not done so NFTs are loaded from chain
    if (!nftMintsTokens) {
        nftMintsTokens = await getNftMints({ provider, nftCreator });
    }
    const escrowRelockerSdk = new sdk_1.EscrowRelockerSDK({
        provider,
    });
    const escrowWrappers = [];
    for (const nftMintToken of nftMintsTokens) {
        const escrowWrapper = await Escrow_1.EscrowWrapper.fromNftMint(escrowRelockerSdk, nftMintToken.nftMint);
        const gaugeVoters = await escrowWrapper.gaugeVoters();
        gaugeVoters.forEach(async (gaugeVoterWrapper) => {
            const txClose = await gaugeVoterWrapper.closeGauges({
                nftMint: nftMintToken.nftMint,
                nftToken: nftMintToken.nftToken,
                // nftOwner: wallet, - not needed, the owner is taken from the provider
            });
            tx = tx.combine(txClose);
        });
        escrowWrappers.push({ escrowWrapper, ...nftMintToken });
    }
    // check existence of owner ATA account and create if not exists
    const ownerTokenAddress = (0, solana_spl_token_modern_1.getAssociatedTokenAddressSync)(splGovRealmMint, wallet);
    try {
        await (0, solana_spl_token_modern_1.getAccount)(provider.connection, ownerTokenAddress);
    }
    catch (e) {
        tx.append((0, solana_spl_token_modern_1.createAssociatedTokenAccountInstruction)(wallet, // payer
        ownerTokenAddress, wallet, // owner
        splGovRealmMint));
    }
    const vsrEntryResult = await getVsrDepositEntry({
        provider,
        splGovRealmMint,
        splGovRealm,
        splGovVsrProgramId,
    });
    const vsrDepositEntryIndex = vsrEntryResult.type === 'NoVoterAccount' ? 0 : vsrEntryResult.index;
    if (vsrEntryResult.type === 'NoVoterAccount' ||
        vsrEntryResult.type === 'New') {
        const { registrar: { pubkey: registrar }, voter: { pubkey: voter, bump: voterBump }, voterWeightRecord: { pubkey: voterWeightRecord, bump: voterWeightRecordBump, }, } = getVsrAddresses({
            wallet,
            splGovRealmMint,
            splGovRealm,
            splGovVsrProgramId,
        });
        const vsrClient = await VsrClient_1.VsrClient.connect(provider, VsrClient_1.MNDE_VSR_PROGRAM_ID);
        if (vsrEntryResult.type === 'NoVoterAccount') {
            // no Voter account exists
            const ix = await vsrClient.program.methods
                .createVoter(voterBump, voterWeightRecordBump)
                .accounts({
                registrar,
                voter,
                voterAuthority: wallet,
                voterWeightRecord,
                payer: wallet,
                systemProgram: web3_js_1.SystemProgram.programId,
                rent: web3_js_1.SYSVAR_RENT_PUBKEY,
                instructions: web3_js_1.SYSVAR_INSTRUCTIONS_PUBKEY,
            })
                .instruction();
            tx.append(ix);
        }
        const splGovRealmData = await (0, spl_governance_1.getRealm)(provider.connection, splGovRealm);
        const splGovRealmProgramId = splGovRealmData.owner;
        const tokenOwnerInstructions = [];
        const tokenOwnerRecordAddress = await (0, spl_governance_1.withCreateTokenOwnerRecord)(tokenOwnerInstructions, splGovRealmProgramId, spl_governance_1.PROGRAM_VERSION_V3, // TODO: probably not hardcode this
        splGovRealm, // realm address
        wallet, // owner
        splGovRealmMint, // mint
        wallet // payer
        );
        if ((await provider.getAccountInfo(tokenOwnerRecordAddress)) === null) {
            // token owner record does not exist, we need to have it for the user may vote
            tx.append(...tokenOwnerInstructions);
        }
        const vsrVoterVault = (0, VsrClient_1.vaultAddress)({ voter, mint: splGovRealmMint });
        const ix = await vsrClient.program.methods
            // period is 30, as LockupKind.constant has got period_secs set to SECS_PER_DAY
            .createDepositEntry(vsrDepositEntryIndex, { constant: {} }, new bn_js_1.BN(0), 30, false)
            .accounts({
            registrar,
            voter,
            vault: vsrVoterVault,
            voterAuthority: wallet,
            payer: wallet,
            depositMint: splGovRealmMint,
            systemProgram: web3_js_1.SystemProgram.programId,
            tokenProgram: solana_spl_token_modern_1.TOKEN_PROGRAM_ID,
            associatedTokenProgram: solana_spl_token_modern_1.ASSOCIATED_TOKEN_PROGRAM_ID,
            rent: web3_js_1.SYSVAR_RENT_PUBKEY,
        })
            .instruction();
        tx.append(ix);
    }
    return { transactions: tx.partition(), escrowWrappers, vsrDepositEntryIndex };
}
exports.migratePrepare = migratePrepare;
/**
 * Creates transactions to migrate NFTs owned by the provider.wallet to the VSR.
 * Transactions consists of atomic pair of instructions `migrate` (from escrow relocker)
 * and `deposit` (from VSR).
 *
 * @param provider Solana provider of @saberhq/solana-contrib, the wallet key in the provider is taken as the owner of NFTs to be migrated
 * @param nftCreator NFT creator address that filters what NFTs, owned by provider.wallet, are migrated
 * @param splGovRealmMint SPL Governance Realm mint to migrate with, default set to MNDE mint
 * @param splGovRealm SPL Governance Realm address to migrate to, default set to MNDE Realm
 * @param splGovVsrProgramId  SPL Governance Voter Stage Registry program ID configured at Realm, default set to one at MNDE Realm
 */
async function migrate({ provider, nftCreator = exports.MNDE_NFT_CREATOR, splGovRealmMint = exports.MNDE_MINT, splGovRealm = exports.MNDE_REALM, splGovVsrProgramId = VsrClient_1.MNDE_VSR_PROGRAM_ID, vsrDepositIndex, escrowWrappers, }) {
    if (!vsrDepositIndex) {
        const vsrEntryResult = await getVsrDepositEntry({
            provider,
            splGovRealmMint,
            splGovRealm,
            splGovVsrProgramId,
        });
        // the migrate operation does not fail when entry does not exist
        // it's the responsibility of the caller to ensure it exists
        vsrDepositIndex =
            vsrEntryResult.type === 'NoVoterAccount' ? 0 : vsrEntryResult.index;
    }
    const wallet = provider.wallet.publicKey;
    const txs = [];
    const escrowRelockerSdk = new sdk_1.EscrowRelockerSDK({
        provider,
    });
    const vsrClient = await VsrClient_1.VsrClient.connect(provider, splGovVsrProgramId);
    const { registrar: { pubkey: registrar }, voter: { pubkey: voter }, } = getVsrAddresses({
        wallet,
        splGovRealmMint,
        splGovRealm,
        splGovVsrProgramId,
    });
    const vsrVoterVault = (0, VsrClient_1.vaultAddress)({ voter, mint: splGovRealmMint });
    if (!escrowWrappers) {
        escrowWrappers = [];
        const nftMintsTokens = await getNftMints({ provider, nftCreator });
        for (const nftMintToken of nftMintsTokens) {
            const escrowWrapper = await Escrow_1.EscrowWrapper.fromNftMint(escrowRelockerSdk, nftMintToken.nftMint);
            escrowWrappers.push({ escrowWrapper, ...nftMintToken });
        }
    }
    const ownerTokenAddress = (0, solana_spl_token_modern_1.getAssociatedTokenAddressSync)(splGovRealmMint, wallet);
    for (const escrowWrapperWithMint of escrowWrappers) {
        let tx = new solana_contrib_1.TransactionEnvelope(provider, [], []);
        const escrowWrapper = escrowWrapperWithMint.escrowWrapper;
        const escrowData = await escrowWrapper.data();
        const txEnvelope = await escrowWrapper.migrate({
            nftToken: escrowWrapperWithMint.nftToken,
            govMint: splGovRealmMint,
            // nftOwner: MIGRATION_TEST_USER, : when not specified the PROVIDER wallet pubkey is used
        });
        tx = tx.combine(txEnvelope);
        const ixDeposit = await vsrClient.program.methods
            .deposit(vsrDepositIndex, escrowData.amount)
            .accounts({
            registrar,
            voter,
            vault: vsrVoterVault,
            depositAuthority: wallet,
            tokenProgram: solana_spl_token_modern_1.TOKEN_PROGRAM_ID,
            depositToken: ownerTokenAddress,
        })
            .instruction();
        tx.append(ixDeposit);
        appendTransaction(tx, txs);
    }
    return txs;
}
exports.migrate = migrate;
function getVsrAddresses({ wallet, splGovRealmMint, splGovRealm, splGovVsrProgramId, }) {
    // a new voter account for the user
    const [registrarAddr, registrarBump] = (0, VsrClient_1.registrarAddress)({
        mint: splGovRealmMint,
        realm: splGovRealm,
        programId: splGovVsrProgramId,
    });
    const [voterAddr, voterBump] = (0, VsrClient_1.voterAddress)({
        programId: splGovVsrProgramId,
        registrar: registrarAddr,
        voterAuthority: wallet,
    });
    const [voterWeightRecordAddr, voterWeightRecordBump] = (0, VsrClient_1.voterWeightRecordAddress)({
        programId: splGovVsrProgramId,
        registrar: registrarAddr,
        voterAuthority: wallet,
    });
    return {
        registrar: {
            pubkey: registrarAddr,
            bump: registrarBump,
        },
        voter: {
            pubkey: voterAddr,
            bump: voterBump,
        },
        voterWeightRecord: {
            pubkey: voterWeightRecordAddr,
            bump: voterWeightRecordBump,
        },
    };
}
/**
 * Search VSR mango plugin for Voter account deposit entry.
 * @returns {VsrDepositEntryResult} - Returns type 'Existing' with mint entry with 30 days lockup type is found,
 *                                    type 'New' when not proprietary entry is found but there is empty place to create new
 *                                    and type 'NoVoterAccount' when no `Voter` account exists.
 */
async function getVsrDepositEntry({ provider, splGovRealmMint, splGovRealm, splGovVsrProgramId, }) {
    const wallet = provider.wallet.publicKey;
    // a new voter account for the user
    const { registrar: { pubkey: registrar }, voter: { pubkey: voter }, } = getVsrAddresses({
        wallet,
        splGovRealmMint,
        splGovRealm,
        splGovVsrProgramId,
    });
    const vsrClient = await VsrClient_1.VsrClient.connect(provider, splGovVsrProgramId);
    const registrarData = await vsrClient.program.account.registrar.fetch(registrar);
    const registrarVotingMints = registrarData.votingMints;
    let vsrMintConfigIdx = undefined;
    for (let i = 0; i < registrarVotingMints.length; i++) {
        if (registrarVotingMints[i].mint.equals(splGovRealmMint)) {
            vsrMintConfigIdx = i;
            break;
        }
    }
    if (vsrMintConfigIdx === undefined) {
        throw new Error(`Mint ${splGovRealmMint.toBase58()} is not configured in registrar ${registrar.toBase58()} at realm ${registrarData.realm.toBase58()}`);
    }
    const voterData = await vsrClient.program.account.voter.fetchNullable(voter);
    if (!voterData) {
        return { type: 'NoVoterAccount' };
    }
    // finding deposit entry with defined mint and with constant lockup type with 30 days lockup
    const deposits = voterData.deposits;
    let availableDepositEntryIdx = undefined;
    for (let i = 0; i < deposits.length; i++) {
        if (deposits[i].votingMintConfigIdx === vsrMintConfigIdx &&
            deposits[i].lockup.kind.constant &&
            deposits[i].lockup.endTs.sub(deposits[i].lockup.startTs).toNumber() ===
                exports.THIRTY_DAYS_IN_SECONDS) {
            return { type: 'Existing', index: i };
        }
        if (!deposits[i].isUsed) {
            // the last available index will be used
            availableDepositEntryIdx = i;
        }
    }
    if (availableDepositEntryIdx === undefined) {
        throw new Error(`No mint ${splGovRealmMint.toBase58()} deposit entry with constant 30 days type ` +
            `or an empty entry was found for voter ${voter.toBase58()}`);
    }
    return { type: 'New', index: availableDepositEntryIdx };
}
async function getNftMints({ provider, nftCreator = exports.MNDE_NFT_CREATOR, }) {
    const wallet = provider.wallet.publicKey;
    const metaplex = new js_1.Metaplex(provider.connection);
    const nftsByOwner = await metaplex.nfts().findAllByOwner({ owner: wallet });
    const nftsMintsByOwner = nftsByOwner
        .filter(nft => nft.creators.find(creator => creator.address.equals(nftCreator)) !==
        undefined)
        .map(nft => {
        const nftMint = 'mint' in nft ? nft.mint.address : nft.mintAddress;
        const nftToken = (0, solana_spl_token_modern_1.getAssociatedTokenAddressSync)(nftMint, wallet, true);
        return { nftMint, nftToken };
    });
    if (nftsMintsByOwner.length < 1) {
        throw new Error(`Not matching NFTs for user ${wallet.toBase58()}`);
    }
    return nftsMintsByOwner;
}
function appendTransaction(newTx, list) {
    if (newTx.estimateSizeUnsafe() > solana_contrib_1.PACKET_DATA_SIZE) {
        throw new Error('Cannot proceed with migration - migrate+deposit instruction pair is too big for a transaction');
    }
    if (list.length === 0) {
        list.push(newTx);
        return;
    }
    const lastTx = list[list.length - 1];
    const estimatedTx = lastTx.combine(newTx);
    if (estimatedTx.estimateSizeUnsafe() <= solana_contrib_1.PACKET_DATA_SIZE) {
        list[list.length - 1] = estimatedTx;
    }
    else {
        list.push(newTx);
    }
}
//# sourceMappingURL=Migrate.js.map